import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Grid2 as Grid, FormGroup, Grid2Props } from "@mui/material";
import EntitySelectionInput, {
  EntitySelectionProps,
} from "../../../entitiesReferences/EntitySelectionInput";
import { singleGridItem } from "../defaultStyles";
import { useInputFullName } from "../subForms/NamePrefixContext";
import FormLabel from "../../common/base/FormLabel";
import UserAttributeMetadataPaper from "../../attributeMetadata/UserAttributeMetadataPaper";
import { SystemEntityCollections } from "../../../../shared/enums/shared-enums";
import { useFormHandlerContext } from "../../../../controllers/formController";

interface NodeEntity {
  published_id: string;
  _key: string;
}

export const extractReferenceKey = <T extends NodeEntity>(
  ref: Partial<T> | undefined | null
): string | null => ref?._key || null;

interface FormEntitySelectionFieldProps<T extends NodeEntity>
  extends Grid2Props {
  name?: string;
  disabled?: boolean;
  label: string;
  entityCollection: SystemEntityCollections;
  required?: boolean;
  placeholder?: string;
  buttonLabel?: string;
  labelActions?: React.ReactNode;
  renderEntitySelection: (props: EntitySelectionProps<T>) => React.ReactNode;
  validate?: (value: any) => string | boolean;
}

const FormEntitySelectionField = <T extends NodeEntity>({
  name,
  label,
  labelActions,
  entityCollection,
  required,
  placeholder,
  buttonLabel,
  disabled,
  renderEntitySelection,
  validate,
  ...grid
}: FormEntitySelectionFieldProps<T>) => {
  const { isProcessing } = useFormHandlerContext();
  const { control } = useFormContext();
  const fullName = useInputFullName(name);

  return (
    <Grid {...singleGridItem} {...grid}>
      <UserAttributeMetadataPaper name={fullName}>
        <Controller
          name={fullName}
          control={control}
          rules={{
            required: required ? `${label} is required` : false,
            validate,
          }}
          render={({ field, fieldState }) => {
            return (
              <FormGroup>
                <FormLabel
                  required={required}
                  label={label}
                  name={fullName}
                  actions={labelActions}
                />
                <EntitySelectionInput
                  {...field}
                  id={fullName}
                  entityCollection={entityCollection}
                  buttonLabel={buttonLabel}
                  disabled={disabled || isProcessing}
                  required={required}
                  placeholder={placeholder || `Select ${label}`}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  renderEntitySelection={renderEntitySelection}
                  fullWidth
                />
              </FormGroup>
            );
          }}
        />
      </UserAttributeMetadataPaper>
    </Grid>
  );
};

export default FormEntitySelectionField;
